import { Box } from '@mui/material';
import type { WorkshopSettings } from '@/types';
import type { ReadinessSection } from '@/features/settings/workshopReadiness';
import type { WorkshopSettingsSaveGroup } from '@/features/settings/workshopSettingsPayload';
import type { SettingsTab } from '@/features/settings/settingsTabs';
import { ServiceBookLinksSettings } from './ServiceBookLinksSettings';
import { AdditionalQuestionsSettings } from './AdditionalQuestionsSettings';
import { WorkshopReadinessAlert } from './workshop/WorkshopReadinessAlert';
import { WorkshopSectionIdentity } from './workshop/WorkshopSectionIdentity';
import { WorkshopSectionBilling } from './workshop/WorkshopSectionBilling';
import { WorkshopSectionHoursExceptions } from './workshop/WorkshopSectionHoursExceptions';
import { WorkshopSectionSurcharge } from './workshop/WorkshopSectionSurcharge';
import { WorkshopSectionPricing } from './workshop/WorkshopSectionPricing';
import { LaborRatesSettings } from './workshop/LaborRatesSettings';
import { WorkshopSectionHours } from './workshop/WorkshopSectionHours';
import { WorkshopSectionScheduling } from './workshop/WorkshopSectionScheduling';
import { WorkshopSectionQuotes } from './workshop/WorkshopSectionQuotes';

const GROUP_TAB: Record<WorkshopSettingsSaveGroup, SettingsTab> = {
  identity: 'workshop',
  pricing: 'workshop-pricing',
  hours: 'workshop-hours',
};

interface WorkshopSettingsFormProps {
  settings: WorkshopSettings;
  onChange: (settings: WorkshopSettings) => void;
  /** Not part of WorkshopSettings; held by the page and passed to billing. */
  paymentTermsDaysOptions: number[];
  onPaymentTermsChange: (value: number[]) => void;
  /** Which workshop page this instance is. Each has its own save (#2428). */
  group: WorkshopSettingsSaveGroup;
}

/**
 * One workshop settings page: section order for that save group (#2465, #2428).
 *
 * Identity, pricing and hours used to share one tab and one PATCH. The
 * sections still live here so the order of each page stays visible at a
 * glance; the page chooses which group to render.
 */
export function WorkshopSettingsForm({
  settings,
  onChange,
  paymentTermsDaysOptions,
  onPaymentTermsChange,
  group,
}: WorkshopSettingsFormProps) {
  /**
   * The readiness alert names a section; these anchors are how it gets there
   * when that section is on this page. Gaps on another workshop page navigate.
   */
  const jumpToSection = (section: ReadinessSection) => {
    document
      .getElementById(`workshop-section-${section}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box>
      <WorkshopReadinessAlert
        settings={settings}
        currentTab={GROUP_TAB[group]}
        onJumpToSection={jumpToSection}
      />

      {group === 'identity' && (
        <>
          <Box id="workshop-section-identity" sx={{ scrollMarginTop: 16 }}>
            <WorkshopSectionIdentity settings={settings} onChange={onChange} />
          </Box>

          {/*
            No cast here any more (#2618). Widening the type at the call site kept
            these out of WorkshopSettings, which is exactly what let the payload
            key map skip them — the values were edited and never sent.
          */}
          <AdditionalQuestionsSettings
            questions={settings.additionalQuestions ?? []}
            onChange={(additionalQuestions) => onChange({ ...settings, additionalQuestions })}
          />

          <ServiceBookLinksSettings
            links={settings.serviceBookLinks ?? []}
            onChange={(serviceBookLinks) => onChange({ ...settings, serviceBookLinks })}
          />
        </>
      )}

      {group === 'pricing' && (
        <>
          <Box id="workshop-section-billing" sx={{ scrollMarginTop: 16 }}>
            <WorkshopSectionBilling
              settings={settings}
              onChange={onChange}
              paymentTermsDaysOptions={paymentTermsDaysOptions}
              onPaymentTermsChange={onPaymentTermsChange}
            />
          </Box>

          <Box id="workshop-section-pricing" sx={{ scrollMarginTop: 16 }}>
            <WorkshopSectionPricing settings={settings} onChange={onChange} />
            <LaborRatesSettings />
          </Box>

          <WorkshopSectionSurcharge settings={settings} onChange={onChange} />

          <WorkshopSectionQuotes settings={settings} onChange={onChange} />
        </>
      )}

      {group === 'hours' && (
        <>
          <WorkshopSectionHours settings={settings} onChange={onChange} />

          <WorkshopSectionHoursExceptions />

          <WorkshopSectionScheduling settings={settings} onChange={onChange} />
        </>
      )}
    </Box>
  );
}
